// Agreement Templates and Clause Library for NDIS Service Agreements

import { SUPPORT_CATEGORIES, SUPPORT_TYPES } from './ndis-pricing.js';

// Re-export for convenience
export { SUPPORT_CATEGORIES, SUPPORT_TYPES };

// Standard Clauses Library
export const STANDARD_CLAUSES = {
    'service-delivery': {
        id: 'service-delivery',
        title: 'Service Delivery',
        category: 'core',
        required: true,
        content: `The Provider will deliver services as outlined in this agreement in accordance with the NDIS Practice Standards and the NDIS Code of Conduct. Services will be delivered:

• At the agreed location(s) specified in this agreement
• By the Provider who is appropriately qualified and screened
• In a manner that respects the participant's dignity, privacy, and choices
• To support the participant's goals and individual needs

The Provider has completed required background checks including NDIS Worker Screening and holds current Working with Children Checks where applicable.`
    },

    'participant-rights': {
        id: 'participant-rights',
        title: 'Participant Rights and Responsibilities',
        category: 'core',
        required: true,
        content: `The Participant has the right to:
• Receive services that meet their individual needs and goals
• Be treated with dignity, respect, and courtesy
• Make decisions about their supports and how they are delivered
• Access information about the services provided
• Provide feedback and make complaints without fear of retribution
• Have their privacy and personal information protected
• Cancel or change this agreement as outlined in the cancellation clause

The Participant agrees to:
• Treat the Provider with respect and courtesy
• Provide accurate information relevant to service delivery
• Give required notice for cancellations where possible
• Maintain a safe environment for service delivery (where applicable)
• Communicate any changes to circumstances that may affect services`
    },

    'provider-obligations': {
        id: 'provider-obligations',
        title: 'Provider Obligations',
        category: 'core',
        required: true,
        content: `The Provider agrees to:
• Deliver services as described in this agreement
• Comply with all relevant NDIS legislation, rules, and guidelines
• Maintain NDIS registration (where required) and appropriate insurance
• Be appropriately qualified and screened to provide services
• Provide services safely and competently
• Respect participant choice and control
• Maintain accurate records of services provided
• Invoice accurately and only for services delivered
• Respond to complaints and feedback in a timely manner
• Protect participant privacy and maintain confidentiality
• Provide required reports and documentation`
    },

    'ndis-compliance': {
        id: 'ndis-compliance',
        title: 'NDIS Compliance',
        category: 'regulatory',
        required: true,
        content: `This agreement complies with NDIS requirements including:

• Pricing in accordance with the current NDIS Price Guide and Support Catalogue
• Services are reasonable and necessary supports as per the participant's NDIS plan
• The Provider will only claim for supports actually delivered
• Services will be delivered within the participant's plan period
• The Provider will maintain records for audit purposes

The participant's NDIS plan funds the supports in this agreement. The Provider will claim from the appropriate budget category as specified and will not exceed the available funding without prior agreement.`
    },

    'cancellation-standard': {
        id: 'cancellation-standard',
        title: 'Cancellation Policy - Standard',
        category: 'operational',
        required: false,
        content: `Cancellations and Changes:

The Participant agrees to provide notice for cancellations:
• At least 7 days notice for scheduled services where possible
• For short notice cancellations (less than 7 clear business days), the NDIS cancellation rules apply

The Provider may charge up to 90% of the agreed fee for:
• Cancellations with less than 7 clear business days notice
• Participant no-shows without prior notice

The Provider will not charge a cancellation fee when:
• The cancellation is due to circumstances beyond the participant's control
• The participant is hospitalised
• The Provider is unable to provide services due to illness or other unavoidable circumstances

The Provider will give as much notice as possible if services need to be cancelled or rescheduled due to Provider illness or other factors.`
    },

    'cancellation-short-notice': {
        id: 'cancellation-short-notice',
        title: 'Cancellation Policy - Short Notice',
        category: 'operational',
        required: true,
        content: `Short Notice Cancellation Policy:

In accordance with NDIS Pricing Arrangements:
• Short notice is defined as less than 2 clear business days before the scheduled service
• The Provider may charge 100% of the agreed fee for short notice cancellations
• The Provider may charge 100% of the agreed fee for no-shows

The Provider will:
• Keep records of all cancellations and reasons
• Not charge for cancellations due to Provider unavailability
• Consider participant circumstances when applying this policy

The Participant should contact the Provider as soon as possible if they need to cancel.`
    },

    'complaint-handling': {
        id: 'complaint-handling',
        title: 'Feedback and Complaints',
        category: 'core',
        required: true,
        content: `The Provider welcomes feedback and has a formal complaints process:

To provide feedback or make a complaint:
• Contact the Provider directly using the details in this agreement
• The Provider will acknowledge complaints within 2 business days
• The Provider aims to resolve complaints within 21 days

If the participant is not satisfied with the Provider's response, they may contact:
• NDIS Quality and Safeguards Commission: 1800 035 544
• National Disability Abuse and Neglect Hotline: 1800 880 052

The participant will not be disadvantaged for making a complaint and has the right to involve an advocate at any time.`
    },

    'privacy': {
        id: 'privacy',
        title: 'Privacy and Confidentiality',
        category: 'regulatory',
        required: true,
        content: `The Provider will protect participant privacy by:

• Only collecting personal information necessary for service delivery
• Storing information securely in accordance with the Privacy Act 1988
• Not disclosing personal information without consent except where required by law
• Allowing the participant to access their records upon request
• Retaining records for the period required by NDIS and other legislation

The participant consents to the Provider:
• Sharing information with the NDIA as required for claiming and compliance
• Sharing relevant information with other providers involved in the participant's care (with specific consent)
• Using de-identified information for service improvement purposes`
    },

    'termination': {
        id: 'termination',
        title: 'Agreement Termination',
        category: 'operational',
        required: true,
        content: `This agreement may be ended:

By the Participant:
• At any time by providing 14 days written notice to the Provider
• Immediately if the Provider breaches this agreement or the NDIS Code of Conduct

By the Provider:
• With 14 days written notice to the Participant
• Immediately if continuing services would put the Provider or the participant at risk
• If funding for services is no longer available

Upon termination:
• The Provider will invoice for services delivered up to the termination date
• The Provider will provide a summary of services and assist with transition to new providers where requested
• The participant may request copies of their records

This agreement automatically ends when the participant's NDIS plan period ends unless renewed.`
    },

    'amendments': {
        id: 'amendments',
        title: 'Agreement Amendments',
        category: 'operational',
        required: true,
        content: `Changes to this agreement:

Either party may request changes to this agreement at any time. Changes may include:
• Service schedule adjustments
• Location changes
• Pricing updates (in accordance with NDIS Price Guide)
• Support type modifications

For changes to be valid:
• Both parties must agree to the changes in writing
• Material changes will create a new version of this agreement
• Minor schedule changes may be agreed verbally and documented

The Provider will provide updated pricing when the NDIS Price Guide changes and will give notice before implementing new rates.`
    },

    'payment-terms': {
        id: 'payment-terms',
        title: 'Payment and Claiming',
        category: 'financial',
        required: true,
        content: `Payment for services:

For NDIA-Managed participants:
• The Provider will submit claims directly to the NDIS portal
• Claims will be submitted within 60 days of service delivery

For Plan-Managed participants:
• Invoices will be sent to the Plan Manager within 14 days of service delivery
• Invoices will include: Date of service, support item number, description, quantity, and rate

For Self-Managed participants:
• Invoices will be provided to the participant within 14 days of service delivery
• Payment is due within 14 days of invoice date

All invoices will include ABN, itemised services, and NDIS support item numbers.`
    },

    'insurance': {
        id: 'insurance',
        title: 'Insurance',
        category: 'regulatory',
        required: false,
        content: `The Provider maintains the following insurance coverage:

• Public Liability Insurance: Minimum $10 million
• Professional Indemnity Insurance: Minimum $5 million
• Workers Compensation Insurance: As required by law

Certificates of currency are available upon request. The Provider will notify the participant if insurance coverage lapses or changes significantly.`
    },

    'work-health-safety': {
        id: 'work-health-safety',
        title: 'Work Health and Safety',
        category: 'operational',
        required: false,
        content: `The Provider is committed to maintaining a safe environment for participants:

• Risk assessments will be conducted where services are delivered
• The Provider will follow safe work practices and use appropriate equipment
• Incidents will be reported and investigated as required

For services delivered in the participant's home:
• The participant will ensure the environment is safe for the Provider
• The Provider may suspend services if safety cannot be assured
• The Provider will work with the participant to address any safety concerns`
    },

    'conflict-of-interest': {
        id: 'conflict-of-interest',
        title: 'Conflict of Interest',
        category: 'regulatory',
        required: false,
        content: `The Provider will:

• Disclose any actual, perceived, or potential conflicts of interest
• Act in the participant's best interests at all times
• Not make recommendations that primarily benefit the Provider
• Support participant choice even when it means using other providers
• Maintain professional boundaries at all times

The participant has the right to seek services from any registered provider and is not obligated to continue with this Provider.`
    },

    'consent': {
        id: 'consent',
        title: 'Consent',
        category: 'core',
        required: false,
        content: `By signing this agreement, the participant (or their representative) confirms:

• They have read and understood this agreement
• They consent to the services described
• They have had the opportunity to ask questions
• Information provided to the Provider is accurate
• They understand how to make changes or end this agreement

Where the participant has a nominee, guardian, or plan nominee, their signature indicates they have authority to enter this agreement on behalf of the participant.`
    }
};

// Agreement Templates
export const AGREEMENT_TEMPLATES = {
    // Core Supports Templates
    'core-daily-living': {
        id: 'core-daily-living',
        name: 'Daily Living Support',
        category: 'core-supports',
        subcategory: 'daily-activities',
        supportType: 'ongoing',
        description: 'Standard template for assistance with daily personal activities including self-care, meal preparation, and household tasks.',
        suggestedItemNumbers: [
            '01_011_0107_1_1',
            '01_011_0107_1_1_T',
            '01_012_0107_1_1',
            '01_015_0107_1_1'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'cancellation-standard',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms'
        ],
        defaultDescription: `Assistance with daily personal activities including:
• Personal care and hygiene support
• Meal preparation and eating
• Household tasks and domestic assistance
• Community access for daily activities
• Building skills for greater independence

Services will be delivered by the Provider who will work with the participant to achieve their goals while respecting their choices and preferences.`,
        defaultSchedule: {
            frequency: 'weekly',
            hoursPerSession: 2,
            sessionsPerWeek: 3
        }
    },

    'core-community-access': {
        id: 'core-community-access',
        name: 'Community Access Support',
        category: 'core-supports',
        subcategory: 'social-community',
        supportType: 'ongoing',
        description: 'Template for support to participate in community, social, and recreational activities.',
        suggestedItemNumbers: [
            '04_104_0125_6_1',
            '04_102_0125_6_1'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'cancellation-standard',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms'
        ],
        defaultDescription: `Support to participate in community, social, and recreational activities including:
• Access to community events and activities
• Social skill development
• Building connections and relationships
• Exploring interests and hobbies
• Transport assistance where included

The Provider will accompany and assist the participant to engage in activities of their choice while building skills for greater independence.`,
        defaultSchedule: {
            frequency: 'weekly',
            hoursPerSession: 4,
            sessionsPerWeek: 1
        }
    },

    'core-transport': {
        id: 'core-transport',
        name: 'Transport Support',
        category: 'core-supports',
        subcategory: 'transport',
        supportType: 'ongoing',
        description: 'Template for transport assistance to access the community.',
        suggestedItemNumbers: [
            '02_051_0108_1_1',
            '02_052_0108_1_1'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'cancellation-standard',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms'
        ],
        defaultDescription: `Transport assistance to enable community access including:
• Transport to appointments and activities
• Support during travel where required
• Door-to-door assistance
• Building travel independence skills

Transport may be provided using the provider's vehicle or by assisting with public transport.`,
        defaultSchedule: {
            frequency: 'as-required',
            hoursPerSession: 1,
            sessionsPerWeek: 2
        }
    },

    // Capacity Building Templates
    'cb-support-coordination': {
        id: 'cb-support-coordination',
        name: 'Support Coordination',
        category: 'capacity-building',
        subcategory: 'support-coordination',
        supportType: 'ongoing',
        description: 'Template for support coordination services to help understand and implement NDIS plans.',
        suggestedItemNumbers: [
            '07_001_0106_8_3',
            '07_002_0106_8_3',
            '07_004_0132_8_3'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'cancellation-standard',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms',
            'conflict-of-interest'
        ],
        defaultDescription: `Support coordination services to strengthen abilities to coordinate and implement supports including:
• Understanding the NDIS plan and funded supports
• Connecting with service providers
• Building capacity to self-manage supports
• Coordinating multiple providers
• Preparing for plan reviews
• Crisis support and problem solving
• Monitoring plan implementation

The Support Coordinator will work collaboratively with the participant, their support network, and other providers to ensure the participant achieves their goals.`,
        defaultSchedule: {
            frequency: 'monthly',
            hoursPerSession: 1,
            sessionsPerWeek: 1
        }
    },

    'cb-therapy-ot': {
        id: 'cb-therapy-ot',
        name: 'Occupational Therapy',
        category: 'capacity-building',
        subcategory: 'improved-daily-living',
        supportType: 'episodic',
        description: 'Template for occupational therapy assessment and intervention.',
        suggestedItemNumbers: [
            '15_037_0117_1_3'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'cancellation-standard',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms'
        ],
        defaultDescription: `Occupational therapy services including:
• Functional assessments
• Therapy interventions to improve daily living skills
• Equipment recommendations and trials
• Home and environment assessments
• Training for participant and carers
• Report writing and recommendations

Services will be delivered by a registered Occupational Therapist who will work with the participant to achieve their functional goals.`,
        defaultSchedule: {
            frequency: 'fortnightly',
            hoursPerSession: 1,
            sessionsPerWeek: 1
        }
    },

    'cb-therapy-psychology': {
        id: 'cb-therapy-psychology',
        name: 'Psychology Services',
        category: 'capacity-building',
        subcategory: 'improved-daily-living',
        supportType: 'episodic',
        description: 'Template for psychology assessment and therapy services.',
        suggestedItemNumbers: [
            '15_040_0117_1_3'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'cancellation-standard',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms'
        ],
        defaultDescription: `Psychology services including:
• Psychological assessment
• Individual therapy
• Behaviour support
• Skill development
• Report writing

Services will be delivered by a registered Psychologist who will work with the participant to achieve their goals.`,
        defaultSchedule: {
            frequency: 'weekly',
            hoursPerSession: 1,
            sessionsPerWeek: 1
        }
    },

    'cb-employment': {
        id: 'cb-employment',
        name: 'Employment Support',
        category: 'capacity-building',
        subcategory: 'employment',
        supportType: 'ongoing',
        description: 'Template for finding and keeping employment services.',
        suggestedItemNumbers: [
            '10_009_0133_5_3',
            '10_016_0133_5_3'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'cancellation-standard',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms'
        ],
        defaultDescription: `Employment support services including:
• Career planning and goal setting
• Job searching and applications
• Resume and interview preparation
• Workplace assessments
• On-the-job support and training
• Employer liaison

Services aim to help the participant find and maintain meaningful employment aligned with their goals and interests.`,
        defaultSchedule: {
            frequency: 'weekly',
            hoursPerSession: 2,
            sessionsPerWeek: 1
        }
    },

    // Capital Supports Templates
    'capital-at': {
        id: 'capital-at',
        name: 'Assistive Technology',
        category: 'capital-supports',
        subcategory: 'assistive-tech',
        supportType: 'one-off',
        description: 'Template for assistive technology assessment, purchase, and setup.',
        suggestedItemNumbers: [
            '05_060122_0103_1_2',
            '05_1500_0103_1_2'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'complaint-handling',
            'privacy',
            'termination',
            'payment-terms'
        ],
        defaultDescription: `Assistive technology services including:
• AT needs assessment
• Equipment recommendation
• Equipment supply and setup
• Training on equipment use
• Follow-up and adjustments

The Provider will work with the participant to identify the most suitable equipment to meet their needs and goals.`,
        defaultSchedule: {
            frequency: 'one-off',
            hoursPerSession: 0,
            sessionsPerWeek: 0
        }
    },

    'capital-home-mods': {
        id: 'capital-home-mods',
        name: 'Home Modifications',
        category: 'capital-supports',
        subcategory: 'home-mods',
        supportType: 'one-off',
        description: 'Template for home modification assessment and works.',
        suggestedItemNumbers: [
            '05_18000610_0103_1_2'
        ],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'complaint-handling',
            'privacy',
            'termination',
            'payment-terms'
        ],
        defaultDescription: `Home modification services including:
• Home accessibility assessment
• Modification recommendations
• Quote provision
• Modification works
• Completion inspection

All modifications will be completed by licensed tradespeople and will comply with relevant building codes and standards.`,
        defaultSchedule: {
            frequency: 'one-off',
            hoursPerSession: 0,
            sessionsPerWeek: 0
        }
    },

    // SDA Template
    'sda-accommodation': {
        id: 'sda-accommodation',
        name: 'Specialist Disability Accommodation',
        category: 'sda',
        subcategory: 'fully-accessible',
        supportType: 'ongoing',
        description: 'Template for Specialist Disability Accommodation services.',
        suggestedItemNumbers: [],
        defaultClauses: [
            'service-delivery',
            'participant-rights',
            'provider-obligations',
            'ndis-compliance',
            'complaint-handling',
            'privacy',
            'termination',
            'amendments',
            'payment-terms'
        ],
        defaultDescription: `Specialist Disability Accommodation including:
• Accessible dwelling meeting SDA design standards
• Appropriate design category features
• Shared or individual living arrangements as agreed
• Common area access
• Required assistive technology integration

This agreement covers the SDA component only. Support services are covered under separate agreements.`,
        defaultSchedule: {
            frequency: 'ongoing',
            hoursPerSession: 0,
            sessionsPerWeek: 0
        }
    }
};

// Helper Functions

/**
 * Get template by ID
 * @param {string} templateId - Template ID
 * @returns {Object|null} Template or null if not found
 */
export function getTemplate(templateId) {
    return AGREEMENT_TEMPLATES[templateId] || null;
}

/**
 * Get templates by category
 * @param {string} category - Category key
 * @returns {Array} Templates in the category
 */
export function getTemplatesByCategory(category) {
    return Object.values(AGREEMENT_TEMPLATES).filter(t => t.category === category);
}

/**
 * Get clause by ID
 * @param {string} clauseId - Clause ID
 * @returns {Object|null} Clause or null if not found
 */
export function getClause(clauseId) {
    return STANDARD_CLAUSES[clauseId] || null;
}

/**
 * Get all clauses for a template
 * @param {string} templateId - Template ID
 * @returns {Array} Clause objects
 */
export function getClausesForTemplate(templateId) {
    const template = getTemplate(templateId);
    if (!template) return [];

    return template.defaultClauses.map(id => getClause(id)).filter(Boolean);
}

/**
 * Get required clauses
 * @returns {Array} Required clause objects
 */
export function getRequiredClauses() {
    return Object.values(STANDARD_CLAUSES).filter(c => c.required);
}

/**
 * Get optional clauses
 * @returns {Array} Optional clause objects
 */
export function getOptionalClauses() {
    return Object.values(STANDARD_CLAUSES).filter(c => !c.required);
}

/**
 * Generate agreement number
 * @param {number} sequence - Sequential number
 * @returns {string} Agreement number (SA-YYYY-XXXX)
 */
export function generateAgreementNumber(sequence = 1) {
    const year = new Date().getFullYear();
    const paddedSeq = String(sequence).padStart(4, '0');
    return `SA-${year}-${paddedSeq}`;
}

/**
 * Get all available templates as options for selection UI
 * @returns {Array} Template options grouped by category
 */
export function getTemplateOptions() {
    const grouped = {};

    Object.values(AGREEMENT_TEMPLATES).forEach(template => {
        const categoryInfo = SUPPORT_CATEGORIES[template.category];
        if (!grouped[template.category]) {
            grouped[template.category] = {
                category: template.category,
                categoryName: categoryInfo?.name || template.category,
                color: categoryInfo?.color || '#5C5650',
                templates: []
            };
        }
        grouped[template.category].templates.push({
            id: template.id,
            name: template.name,
            description: template.description,
            supportType: template.supportType
        });
    });

    return Object.values(grouped);
}

/**
 * Get support type display info
 * @param {string} typeKey - Support type key
 * @returns {Object} Support type info
 */
export function getSupportTypeInfo(typeKey) {
    return SUPPORT_TYPES[typeKey] || null;
}

/**
 * Frequency options for schedule
 */
export const FREQUENCY_OPTIONS = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'fortnightly', label: 'Fortnightly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'as-required', label: 'As Required' },
    { value: 'one-off', label: 'One-off' }
];

/**
 * Location options for service delivery
 */
export const LOCATION_OPTIONS = [
    { value: 'participant-home', label: "Participant's Home" },
    { value: 'community', label: 'Community' },
    { value: 'provider-premises', label: 'Provider Premises' },
    { value: 'workplace', label: 'Workplace' },
    { value: 'telehealth', label: 'Telehealth/Remote' },
    { value: 'multiple', label: 'Multiple Locations' }
];

/**
 * Status options for agreements
 */
export const AGREEMENT_STATUS = {
    'draft': {
        label: 'Draft',
        color: '#5C5650',
        bgColor: '#F0EFED'
    },
    'pending_signature': {
        label: 'Pending Signature',
        color: '#B45309',
        bgColor: '#FEF3C7'
    },
    'partially_signed': {
        label: 'Partially Signed',
        color: '#1D4ED8',
        bgColor: '#DBEAFE'
    },
    'active': {
        label: 'Active',
        color: '#047857',
        bgColor: '#D1FAE5'
    },
    'expired': {
        label: 'Expired',
        color: '#6B7280',
        bgColor: '#F3F4F6'
    },
    'cancelled': {
        label: 'Cancelled',
        color: '#DC2626',
        bgColor: '#FEE2E2'
    },
    'amended': {
        label: 'Amended',
        color: '#7C3AED',
        bgColor: '#EDE9FE'
    }
};

/**
 * Get status display info
 * @param {string} status - Status key
 * @returns {Object} Status display info
 */
export function getStatusInfo(status) {
    return AGREEMENT_STATUS[status] || AGREEMENT_STATUS['draft'];
}
